/**
 * Coverage — buyer-facing scorecard. Hero strip from /research/scorecard
 * (P2; falls back to local counts on 404), then Heatmap / Frameworks /
 * Gaps tabs over the same detection catalog.
 */
import { useMemo, useState } from "react";
import { ConnectorGate } from "@/components/connectors/ConnectorGate";
import { SurfaceShell, StatTile } from "@/components/surface/SurfaceShell";
import { Panel } from "@/components/surface/Panel";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useEdrCoverageCatalog,
  useEdrSigmaRules,
  useEdrAlerts,
  useEdrScorecard,
} from "@/hooks/security/useEdrData";
import type { DetectionRule } from "@/types/edr";
import type {
  ScorecardFrameworkDTO,
  ScorecardTacticDTO,
} from "@/types/edrApi";
import { ShieldOff, Target } from "lucide-react";

const TACTICS = [
  "Reconnaissance",
  "Initial Access",
  "Execution",
  "Persistence",
  "Privilege Escalation",
  "Defense Evasion",
  "Credential Access",
  "Discovery",
  "Lateral Movement",
  "Collection",
  "Command & Control",
  "Exfiltration",
  "Impact",
];

export default function CoveragePage() {
  return (
    <ConnectorGate connectorId="edr">
      <CoverageInner />
    </ConnectorGate>
  );
}

function CoverageInner() {
  const catalogQ = useEdrCoverageCatalog();
  const sigmaQ = useEdrSigmaRules();
  const alertsQ = useEdrAlerts({ limit: 500 });
  const scorecardQ = useEdrScorecard();

  const detections = (catalogQ.data?.detections ?? []) as DetectionRule[];
  const sigma = sigmaQ.data ?? [];
  const alerts = alertsQ.data ?? [];
  const scorecard = scorecardQ.data;
  const headline = scorecard?.headline ?? {};
  const frameworks = scorecard?.frameworks ?? [];
  const tactics = scorecard?.tactics ?? [];
  const scorecardOk = !scorecardQ.isError && !!scorecard;

  const [tactic, setTactic] = useState<string | null>(null);
  const [q, setQ] = useState("");

  // Local tactic counts (fallback + heatmap intensity)
  const byTactic = useMemo(() => {
    const map = new Map<string, number>();
    detections.forEach((d) => {
      const t = d.tactic ?? "Other";
      map.set(t, (map.get(t) ?? 0) + 1);
    });
    return map;
  }, [detections]);

  const fired = useMemo(() => {
    const map = new Map<string, number>();
    alerts.forEach((a) => map.set(a.ruleId, (map.get(a.ruleId) ?? 0) + 1));
    return map;
  }, [alerts]);

  const filtered = useMemo(() => {
    return detections
      .filter((d) => (tactic ? d.tactic === tactic : true))
      .filter((d) =>
        q
          ? `${d.name} ${d.mitre} ${d.tactic ?? ""}`.toLowerCase().includes(q.toLowerCase())
          : true,
      );
  }, [detections, tactic, q]);

  const covered = TACTICS.filter((t) => byTactic.get(t)).length;
  const coveragePct = Math.round((covered / TACTICS.length) * 100);

  // KPI tile values prefer scorecard headline; fall back to local counts.
  const kpiDetections = num(headline.detectionsShipping, detections.length);
  const kpiTactics = num(headline.mitreTactics, covered);
  const kpiTechniques = num(
    headline.mitreTechniques,
    new Set(
      detections
        .flatMap((d) => d.mitreMappings?.map((m) => m.techniqueId) ?? [d.mitre])
        .filter((t) => t && t !== "—"),
    ).size,
  );
  const kpiFrameworks = num(headline.frameworksMapped, frameworks.length);

  return (
    <SurfaceShell
      overline="Security · Coverage"
      title="What's protecting us"
      summary={
        scorecardOk
          ? `${scorecard?.vendor ?? "Smplify"} ships ${kpiDetections.toLocaleString()} detections across ${kpiTactics} of ${TACTICS.length} ATT&CK tactics, ${kpiTechniques.toLocaleString()} techniques, and ${kpiFrameworks} frameworks.`
          : `${detections.length} detections live across ${covered} of ${TACTICS.length} MITRE tactics. ${sigma.length} Sigma rules also in rotation.`
      }
      error={catalogQ.isError ? catalogQ.error : undefined}
      onRetry={catalogQ.isError ? () => catalogQ.refetch() : undefined}
      kpis={
        <>
          <StatTile label="Detections" value={kpiDetections.toLocaleString()} loading={catalogQ.isLoading} hint="Active in tenant" />
          <StatTile
            label="MITRE coverage"
            value={`${coveragePct}%`}
            loading={catalogQ.isLoading}
            tone={coveragePct >= 70 ? "success" : coveragePct >= 50 ? "warning" : "danger"}
            hint={`${kpiTactics} of ${TACTICS.length} tactics · ${kpiTechniques.toLocaleString()} techniques`}
          />
          <StatTile
            label="Frameworks mapped"
            value={kpiFrameworks}
            loading={scorecardQ.isLoading}
            hint={scorecardOk ? "MITRE, D3FEND, ATT&CK Evals…" : "Awaiting scorecard"}
          />
          <StatTile label="Sigma rules" value={sigma.length.toLocaleString()} loading={sigmaQ.isLoading} hint="Community-sourced" />
        </>
      }
    >
      {scorecardOk && (
        <ScorecardHero headline={headline} loading={scorecardQ.isLoading} />
      )}

      <Tabs defaultValue="heatmap" className="mt-4">
        <TabsList>
          <TabsTrigger value="heatmap">Heatmap</TabsTrigger>
          <TabsTrigger value="frameworks">
            Frameworks
            {frameworks.length > 0 && (
              <span className="ml-1.5 text-[10px] text-muted-foreground tabular-nums">
                {frameworks.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="gaps">Gaps</TabsTrigger>
        </TabsList>

        <TabsContent value="heatmap" className="space-y-4">
          <Panel title="MITRE ATT&CK · tactic coverage" subtitle="Click a tactic to filter the rule library">
            <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-13 gap-1.5">
              {TACTICS.map((t) => {
                const n = byTactic.get(t) ?? 0;
                const active = tactic === t;
                const intensity =
                  n === 0 ? "bg-muted/40 text-muted-foreground border-border"
                  : n < 3 ? "bg-info/10 text-info-muted-foreground border-info/30"
                  : n < 8 ? "bg-info/20 text-info-muted-foreground border-info/40"
                  : "bg-primary/20 text-foreground border-primary/40";
                return (
                  <button
                    key={t}
                    onClick={() => setTactic(active ? null : t)}
                    className={`px-2 py-2.5 rounded text-left border transition-all ${intensity} ${
                      active ? "ring-2 ring-primary ring-offset-1 ring-offset-background" : ""
                    }`}
                  >
                    <div className="text-[10px] leading-tight font-medium uppercase tracking-wider truncate">
                      {t}
                    </div>
                    <div className="mt-1 text-lg font-light tabular-nums leading-none">{n}</div>
                  </button>
                );
              })}
            </div>
          </Panel>

          <Panel
            title={tactic ? `Detections · ${tactic}` : `Detection library · ${filtered.length}`}
            loading={catalogQ.isLoading}
            isError={catalogQ.isError}
            error={catalogQ.error}
            onRetry={() => catalogQ.refetch()}
            empty={!catalogQ.isLoading && !catalogQ.isError && filtered.length === 0}
            emptyTitle={tactic ? "No detections in this tactic" : "No detections yet"}
            emptyDescription={
              tactic
                ? "Try clearing the tactic filter or your search."
                : "Detection catalog will populate when the EDR backend syncs."
            }
            skeleton="table"
            actions={
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter rules…"
                className="h-8 w-[220px] text-xs"
              />
            }
          >
            <div className="overflow-x-auto -mx-4 -my-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Rule</th>
                    <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">MITRE</th>
                    <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Tactic</th>
                    <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">Severity</th>
                    <th className="px-3 py-2 text-right font-semibold uppercase tracking-wider text-[10px] text-muted-foreground">24h</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((d) => (
                    <tr key={d.id} className="border-b border-border/60 hover:bg-muted/40">
                      <td className="px-3 py-2 text-foreground font-medium truncate max-w-[300px]">
                        {d.name}
                      </td>
                      <td className="px-3 py-2 font-mono text-muted-foreground">{d.mitre}</td>
                      <td className="px-3 py-2 text-muted-foreground">{d.tactic ?? "—"}</td>
                      <td className="px-3 py-2">
                        <SeverityChip s={d.severity} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-foreground">
                        {fired.get(d.id) ?? 0}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </TabsContent>

        <TabsContent value="frameworks">
          <FrameworksTab
            frameworks={frameworks}
            sigmaCount={sigma.length}
            loading={scorecardQ.isLoading}
            unavailable={!scorecardOk}
          />
        </TabsContent>

        <TabsContent value="gaps">
          <GapsTab
            tactics={tactics}
            localTactics={byTactic}
            loading={scorecardQ.isLoading || catalogQ.isLoading}
          />
        </TabsContent>
      </Tabs>
    </SurfaceShell>
  );
}

function num(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function ScorecardHero({
  headline,
  loading,
}: {
  headline: Record<string, unknown>;
  loading: boolean;
}) {
  const tiles: Array<{ key: string; label: string }> = [
    { key: "detectionsShipping", label: "Detections shipping" },
    { key: "mitreTactics", label: "ATT&CK tactics" },
    { key: "mitreTechniques", label: "Techniques covered" },
    { key: "frameworksMapped", label: "Frameworks mapped" },
    { key: "aiAgentsCatalog", label: "AI agents catalog" },
    { key: "llmEndpointsCatalog", label: "LLM endpoints" },
    { key: "platformsCovered", label: "Platforms covered" },
    { key: "credentialFiles", label: "Credential files watched" },
  ];
  return (
    <div className="rounded-lg border border-border bg-card/60 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Smplify Score · screenshot-ready
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">
          last 7 days
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 divide-x divide-y md:divide-y-0 divide-border">
        {tiles.map((t) => {
          const v = headline[t.key];
          const display =
            loading ? null
            : typeof v === "number" ? v.toLocaleString()
            : "—";
          return (
            <div key={t.key} className="px-4 py-4">
              {display === null ? (
                <Skeleton className="h-7 w-16" />
              ) : (
                <div className="text-2xl font-light tabular-nums text-foreground leading-none">
                  {display}
                </div>
              )}
              <div className="mt-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                {t.label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function FrameworksTab({
  frameworks,
  sigmaCount,
  loading,
  unavailable,
}: {
  frameworks: ScorecardFrameworkDTO[];
  sigmaCount: number;
  loading: boolean;
  unavailable: boolean;
}) {
  if (unavailable) {
    return (
      <EmptyState
        icon={Target}
        title="Framework mapping not yet available"
        description="The backend hasn't published the scorecard endpoint. Once /research/scorecard returns data, frameworks like MITRE ATT&CK, D3FEND and ATT&CK Evals will appear here."
      />
    );
  }
  if (loading && frameworks.length === 0) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }
  if (frameworks.length === 0) {
    return (
      <EmptyState
        icon={Target}
        title="No frameworks reported"
        description="The scorecard returned an empty framework list."
      />
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {frameworks.map((f, i) => (
        <div
          key={f.framework ?? i}
          className="rounded-lg border border-border bg-card/60 p-4 space-y-3"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-foreground truncate">
                {f.framework ?? "Framework"}
              </div>
              {f.description && (
                <p className="mt-1 text-xs text-muted-foreground line-clamp-3">
                  {f.description}
                </p>
              )}
            </div>
            <Badge variant="outline" className="bg-success-muted text-success-muted-foreground border-success-border flex-none">
              live
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-1 border-t border-border">
            <div>
              <div className="text-lg font-light tabular-nums text-foreground leading-none">
                {(f.rulesMapped ?? 0).toLocaleString()}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                rules mapped
              </div>
            </div>
            <div>
              <div className="text-lg font-light tabular-nums text-foreground leading-none">
                {(f.distinctValues ?? 0).toLocaleString()}
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                {f.dimensionLabel ?? "items"}
              </div>
            </div>
          </div>
        </div>
      ))}
      <div className="rounded-lg border border-dashed border-border bg-card/30 p-4 space-y-3">
        <div className="text-sm font-semibold text-foreground">Sigma Community</div>
        <p className="text-xs text-muted-foreground">
          Community-sourced detection rules running alongside the Smplify ruleset.
        </p>
        <div>
          <div className="text-lg font-light tabular-nums text-foreground leading-none">
            {sigmaCount.toLocaleString()}
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
            rules in rotation
          </div>
        </div>
      </div>
    </div>
  );
}

function GapsTab({
  tactics,
  localTactics,
  loading,
}: {
  tactics: ScorecardTacticDTO[];
  localTactics: Map<string, number>;
  loading: boolean;
}) {
  // Use scorecard tactics when available, otherwise derive from local counts.
  const rows = useMemo(() => {
    if (tactics.length > 0) {
      return tactics.map((t) => ({
        tactic: t.tactic ?? "Unknown",
        ruleCount: t.ruleCount ?? 0,
        alerts: t.recentAlertCount ?? 0,
        gaps: (t.techniques ?? []).filter((tt) => (tt.ruleCount ?? 0) === 0).length,
        coveredTechniques: (t.techniques ?? []).filter((tt) => (tt.ruleCount ?? 0) > 0).length,
        totalTechniques: (t.techniques ?? []).length,
      }));
    }
    return TACTICS.map((t) => ({
      tactic: t,
      ruleCount: localTactics.get(t) ?? 0,
      alerts: 0,
      gaps: 0,
      coveredTechniques: 0,
      totalTechniques: 0,
    }));
  }, [tactics, localTactics]);

  const uncovered = rows.filter((r) => r.ruleCount === 0);
  const thin = rows.filter((r) => r.ruleCount > 0 && r.ruleCount < 3);

  if (loading && rows.length === 0) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (uncovered.length === 0 && thin.length === 0) {
    return (
      <EmptyState
        icon={ShieldOff}
        title="No coverage gaps"
        description="Every ATT&CK tactic has at least three detections mapped to it. Nice work."
      />
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Panel
        title={`Uncovered tactics · ${uncovered.length}`}
        subtitle="No detections currently mapped"
      >
        {uncovered.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3">No uncovered tactics.</p>
        ) : (
          <ul className="divide-y divide-border -mx-4 -my-4">
            {uncovered.map((r) => (
              <li key={r.tactic} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {r.tactic}
                  </div>
                  {r.totalTechniques > 0 && (
                    <div className="text-[11px] text-muted-foreground">
                      {r.totalTechniques} known techniques · 0 covered
                    </div>
                  )}
                </div>
                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30 flex-none">
                  gap
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      <Panel
        title={`Thin coverage · ${thin.length}`}
        subtitle="Fewer than 3 detections — candidates to expand"
      >
        {thin.length === 0 ? (
          <p className="text-xs text-muted-foreground py-3">No thin tactics.</p>
        ) : (
          <ul className="divide-y divide-border -mx-4 -my-4">
            {thin.map((r) => (
              <li key={r.tactic} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {r.tactic}
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    {r.ruleCount} rule{r.ruleCount === 1 ? "" : "s"}
                    {r.alerts > 0 ? ` · ${r.alerts} alerts (7d)` : ""}
                  </div>
                </div>
                <Badge variant="outline" className="bg-warning/10 text-warning-muted-foreground border-warning-border flex-none">
                  thin
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function SeverityChip({ s }: { s: string }) {
  const map: Record<string, string> = {
    critical: "bg-destructive/15 text-destructive border-destructive/30",
    high: "bg-destructive/10 text-destructive border-destructive/20",
    medium: "bg-warning/15 text-warning-muted-foreground border-warning-border",
    low: "bg-info/15 text-info-muted-foreground border-info-border",
    info: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${map[s] ?? map.info}`}>
      {s}
    </span>
  );
}
